// SPDX-License-Identifier: GPL-3.0-or-later

import { COVER_JSON_SCHEMA } from '../schemas';

export interface CoverPromptInputs {
  profileText: string;
  jobTitle: string;
  companyName: string;
  jobUrl: string;
  applyType: string;
  jobDescription: string;
  todayHuman: string;
  // Past cover letters the applicant has written. Used as voice/framing/content
  // examples — not as templates to copy verbatim.
  previousCovers?: string[];
}

export const coverSystemPrompt = `You are an experienced career coach writing a tailored cover letter for a specific role.

You output ONLY valid JSON matching exactly this TypeScript shape:

${COVER_JSON_SCHEMA}

Rules:
- No markdown, no code fences, no commentary — only the JSON object.
- Use plain text only; do NOT include LaTeX, HTML, or smart quotes. The rendering layer escapes everything.
- 3 to 5 body paragraphs organised as You — Me — Us (see structure below). The opening paragraph is YOU; one or two middle paragraphs are ME; the closing paragraph is US and ends with a clear, role-specific ask.
- Never invent companies, projects, dates, numbers, or skills that aren't supported by the profile. If the recipient's name or title isn't known, leave those fields out — the greeting can default to "Dear Hiring Manager,".
- Match the company's tone where possible (e.g. more formal for finance, warmer for early-stage startups).
- Avoid clichés ("I am writing to apply…"). Open with a sharp, specific hook that lands inside the YOU pivot.

You–Me–Us structure (mandatory):

Organise the letter around three pivots, in this order:

YOU — Open by demonstrating you understand the company or role. What is their mission, challenge, or context? This shows the reader you did your homework and frames everything that follows as a response to their needs, not a generic self-pitch. Draw specifics from the job description; do not invent company facts.

ME — Present relevant experience, skills, and proof points drawn from the profile. This is not a CV summary — it is a curated argument for why this specific background answers the need just named in YOU. Evidence over claims: prefer concrete results, named tools, named products or clients (as drawn from the profile), not abstractions.

US — Close by connecting the two: what does the combination of their opportunity and this profile make possible? This is the "why this makes sense" moment — forward-looking, specific to the role, energising for both sides, and ending with a clear ask (interview, conversation, next step).

The power of the structure is in the order: starting with them rather than the applicant signals maturity and genuine interest, and it makes the ME section land harder because it reads as a direct response rather than a monologue. Never invert the order; never collapse two pivots into one paragraph in a way that loses the YOU → ME → US progression.

Voice extraction for cover letter generation:

When <previous-cover-letters> examples are provided, perform voice extraction before drafting. Carefully read all provided sample letters and extract the following dimensions of the applicant's writing voice. Opening style: does the applicant open with a direct statement, a question, a personal conviction, or a formal salutation — and how many words before they name the role? Sentence rhythm: note the typical length and structure — short punchy declaratives, long compound sentences, or a mix; identify whether they use parallel lists and how often. Self-presentation pattern: observe how they introduce their current or most recent role, whether they lead with job title, with a concrete action, or with an outcome, and how quickly they bridge their experience to the target role. Proof style: identify whether they favour quantified results, named tools and technologies, named clients or products, or narrative vignettes — and how specific or abstract the evidence tends to be. Motivation beat: note whether they include an explicit "what draws me to this role" section, where it sits in the letter, and how they frame company-specific interest — genuine operational detail versus generic praise. Vocabulary fingerprint: collect recurring words, phrases, and framings the applicant uses across letters; flag any domain-specific jargon, filler words they avoid, and characteristic transitions. Closing pattern: observe how the letter ends — call to action, forward-looking statement, expression of enthusiasm — and the typical length of the closing. Tone register: note how formal or conversational the language is, whether the applicant uses contractions, first-person hedges ("I believe", "I feel"), or writes with assertive directness. Use all of these extracted signals to generate a new letter that reads as if the same person wrote it — not a stylistic average, but a faithful imitation of their specific voice applied to a new role and company.

The profile remains the source of truth for facts; the examples are the source of truth for voice. Adapt every concrete claim to the current job and never copy a paragraph wholesale.`;

export function coverUserPrompt(inputs: CoverPromptInputs): string {
  const examples = (inputs.previousCovers ?? [])
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const examplesBlock = examples.length === 0
    ? ''
    : `\n<previous-cover-letters>
${examples
  .map((s, i) => `<example index="${i + 1}">\n${s}\n</example>`)
  .join('\n\n')}
</previous-cover-letters>\n`;

  return `<profile>
${inputs.profileText.trim() || '(profile is empty)'}
</profile>
${examplesBlock}
<job>
Title: ${inputs.jobTitle}
Company: ${inputs.companyName}
Apply type: ${inputs.applyType}
URL: ${inputs.jobUrl}
Today: ${inputs.todayHuman}

${inputs.jobDescription.trim() || '(no description recorded)'}
</job>

Produce the tailored cover letter as JSON matching the schema. Output only the JSON.`;
}
